/**
 * Roof claim gate shared by the web customer report (agreement, warranty and
 * photo pages) and the app's Documentation tab (Monday 13376571808). Moved
 * here from `web/routes/customer-report/roofClaimFlow.ts` so both platforms
 * read ONE rule (V62/V92): a second copy in the app is how the two would drift.
 *
 * Pure on purpose - no env import - so it compiles for every platform and its
 * tests need no REACT_APP_COMMON_ENV.
 */
export type RoofClaimCustomerLike = Readonly<Record<string, unknown>>

export const roofClaimFilled = (value: unknown): boolean =>
  typeof value === 'string' && !!value.trim()

/**

 * Generic name/phone defaults are not evidence of a roof claim. Neither are

 * the scope checklist (tear off, felt, valleys... saved as 1 on every new

 * customer: all 983 created in 2026 have them) nor the address/city/zip,

 * which were copied from the customer's own address. Counting them showed the

 * roof claim agreement on every alarm deal.

 */

export const ROOF_CLAIM_EVIDENCE_FIELDS = [
  'roof_claiming_date',

  'roof_claiming_email',

  'roof_claiming_sum',

  'roof_claiming_deductible',

  'roof_claiming_shingle_manufacturer',

  'roof_claiming_style',

  'roof_claiming_color',

  'roof_claiming_drip_color',

  'roof_claiming_age',

  'roof_claiming_layers',

  'roof_claiming_predominate_pitch',

  'roof_claiming_pipe_jack',

  'roof_claiming_chimney_flashing',

  'roof_claiming_chimney_cap',

  'roof_claiming_digital_satellite',

  'roof_claiming_gas_cap',

  'roof_claiming_other_one',

  'roof_claiming_turtle_vent',

  'roof_claiming_ridge_vent',

  'roof_claiming_turbine_vent',

  'roof_claiming_power_attic_vent',

  'roof_claiming_other_two',

  'roof_claiming_other_three',

  'roof_claiming_notes',

  'roof_claiming_documents_notes',

  'roof_claiming_date_loss',

  'roof_claiming_insurance_company',

  'roof_claiming_claim',

  'roof_claiming_client_sign',

  'roof_claiming_client_sign_date',

  'roof_claiming_company_rep_sign',

  'roof_claiming_company_rep_sign_date',

  'roof_claiming_completion_client_sign',

  'roof_claiming_completion_client_sign_date',
] as const

/** Either roof signature freezes the claim details (V92). */
export function roofClaimLocked(customer: RoofClaimCustomerLike): boolean {
  return [
    'roof_claiming_client_sign',
    'roof_claiming_company_rep_sign',
    'roof_claiming_completion_client_sign',
  ].some((field) => roofClaimFilled(customer[field]))
}

export function roofClaimShown(
  products: readonly string[],

  customer: RoofClaimCustomerLike,
): boolean {
  return (
    products.includes('RC') ||
    ROOF_CLAIM_EVIDENCE_FIELDS.some((field) => {
      const value = customer[field]

      return typeof value === 'number'
        ? Number.isFinite(value) && value > 0
        : roofClaimFilled(value)
    })
  )
}

/**
 * The whole gate as the report applies it: roof claim belongs to the main
 * tenant only (`c.IS_MAIN`, passed in to keep this file env-free), then the
 * product/evidence rule above. Web `CustomerReport` and app `Documentation`
 * both call this, so the Roof Claim photo section in the app appears exactly
 * when the web prints the roof claim pages.
 */
export function roofClaimVisible(
  isMain: boolean,
  products: readonly string[],
  customer: RoofClaimCustomerLike,
): boolean {
  return isMain && roofClaimShown(products, customer)
}
